import {
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Text,
  ThickChevronRightIcon,
} from "@radix-ui/themes";
import { useState } from "react";
import { Dot } from "@/components/logo/Dot";
import Link from "next/link";

export const Works = () => {
  return (
    <Flex
      direction={"column"}
      justify={"start"}
      align={"center"}
      gap={"6"}
      className={"mt-32 w-full md:max-w-[768px]"}
      style={
        {
          "--line-offset": "calc((100% - 80px) / 3 * 2 + 80px + 208px - 9px)",
        } as React.CSSProperties
      }
    >
      <Flex direction={"column"} justify={"start"} align={"center"} gap={"1"}>
        <Text
          size={"3"}
          weight={"medium"}
          style={{
            color: "var(--tomato-9)",
            fontWeight: "600",
          }}
        >
          경력 사항
        </Text>
        <Text
          size={"6"}
          style={{
            fontWeight: "600",
            lineHeight: 1.3,
          }}
          align={"center"}
        >
          제품 개발 과정 전반에 참여하며
          <br />
          실무 경험을 쌓아왔습니다.
        </Text>
      </Flex>
      <Grid
        className={
          "relative w-full max-w-[768px] md:grid-cols-3 md:gap-x-10 md:items-start"
        }
      >
        <div
          className={
            "absolute top-[18px] left-2 md:left-auto md:right-[var(--line-offset)] w-[1px] h-[var(--line-height)] bg-[var(--sand-4)] z-0"
          }
          style={
            {
              "--line-height": "calc(100% + 32px)",
            } as React.CSSProperties
          }
        />

        <Flex
          justify={"start"}
          align={"center"}
          gap={"2"}
          className={"w-52 md:justify-self-end z-10"}
        >
          <Box className={"w-[18px] h-[36px]"}>
            <Dot color={"var(--lime-8)"} />
          </Box>
          <Text size={"3"} weight={"medium"} color={"gray"}>
            업무 경험 (총 3년 2개월)
          </Text>
        </Flex>
      </Grid>
      <Flex justify={"center"} wrap={"wrap"} gap={"8"} className={"w-full"}>
        {works.map((item, index) => (
          <WorkItem
            key={item.company}
            {...item}
            isLast={index === works.length - 1}
          />
        ))}
      </Flex>
    </Flex>
  );
};

type Work = {
  period: string;
  periodSum: string;
  company: string;
  position: string;
  links?: { label: string; href: string }[];
  details: { title: string; descriptions?: string[] }[];
};

const works: Work[] = [
  {
    period: "2023.02 ~ 2025.02",
    periodSum: "(2년 1개월)",
    company: "(주)헨지",
    position: "개발팀 리드",
    links: [
      {
        label: "Quick Demo (라이브)",
        href: "/projects/henge",
      },
      {
        label: "Full Demo (Docker)",
        href: "https://gitlab.com/henge-inc/henge-demo",
      },
    ],
    details: [
      {
        title: "MVP 모델 개발, 서비스 고도화",
        descriptions: [
          "Three.js 기반 3D 시각화 · 관리 웹 서비스 개발",
          "Next.js 전환",
        ],
      },
      {
        title: "AWS 아키텍처 설계 · 구축 및 운영",
        descriptions: [
          "실제 서비스 운영 과정에서 드러난 보안 · 트래픽 이슈를 해결",
        ],
      },
      {
        title: "제품 QA 및 성능 최적화",
        descriptions: [
          "피드 검색 p95 기준 응답 시간 2200ms → 850ms 개선",
          "3D 뷰어 렌더링 성능 개선 (PC: 60fps, Mobile: 40fps)",
        ],
      },
      {
        title: "3D 파일 호환성 개선 파이프라인 구축",
        descriptions: [
          "Blender 기반 전처리 시스템 도입",
          "뷰어 렌더링 관련 버그 리포트 약 60% 감소",
        ],
      },
      {
        title: "리소스 접근 제어 강화",
        descriptions: [
          "CloudFront Signed URL, Lambda@Edge 기반 CDN 접근 제어 적용",
        ],
      },
      {
        title: "개발 생산성 · 협업 효율 개선",
        descriptions: [
          "디자인 시스템 라이브러리 분리 및 GitLab CI 기반 자동 배포 파이프라인 구축",
        ],
      },
      {
        title: "데이터 기반 제품 개선 체계 수립",
        descriptions: [
          "Google Analytics 기반 사용자 행동 분석",
          "개선 우선순위 논의 → 적용 → 검증 사이클 정착",
        ],
      },
      {
        title: "초기 서비스 운영 안정화",
        descriptions: [
          "사용자 피드백을 빠르게 반영하며, 초기 사용자 약 500명 안정적 온보딩",
        ],
      },
    ],
  },
  {
    period: "2022.01 ~ 2023.01",
    periodSum: "(1년 1개월)",
    company: "인프라소프트(주)",
    position: "신사업기술팀 사원",
    details: [
      {
        title: "Java · Spring 기반 VMware vSphere 연동 가상머신 관리 기능 개발",
      },
      {
        title: "공기업 · 금융권 고객사 요구사항에 따른 기능 커스터마이징",
      },
      {
        title: "보안 기능 확인서 1차 심사 통과",
      },
      {
        title: "현장 배포 및 장애 대응 프로세스 개선",
      },
    ],
  },
];

const WorkItem = ({
  period,
  periodSum,
  company,
  position,
  links,
  details,
  isLast,
}: Work & { isLast?: boolean }) => {
  const [openDetails, setOpenDetails] = useState<boolean>(false);

  return (
    <Grid
      className={
        "relative w-full max-w-[768px] md:grid-cols-3 md:gap-x-10 md:items-start"
      }
    >
      <div
        className={
          "absolute top-[18px] left-2 md:left-auto md:right-[var(--line-offset)] w-[1px] h-[var(--line-height)] bg-[var(--sand-4)] z-0"
        }
        style={
          {
            "--line-height": isLast ? "calc(100% - 18px)" : "calc(100% + 48px)",
          } as React.CSSProperties
        }
      />

      <Flex
        justify={"start"}
        align={"center"}
        gap={"2"}
        className={"w-full md:w-52 md:justify-self-end z-10"}
      >
        <Flex
          justify={"center"}
          align={"center"}
          className={"w-[18px] h-[36px]"}
        >
          <Box className={"scale-50"}>
            <Dot color={"var(--sand-6)"} />
          </Box>
        </Flex>
        <Flex className={"work-period-group"}>
          <Text size={"3"} color={"gray"}>
            {period}
          </Text>
          <Text size={"2"} color={"gray"}>
            {periodSum}
          </Text>
        </Flex>
      </Flex>
      <Flex direction={"column"} className={"pl-7 md:pl-0 md:col-span-2"}>
        <Flex direction={"column"} gap={"1"} className={"mb-2"}>
          <Text size={"4"} style={{ fontWeight: 600 }}>
            {company}
          </Text>
          <Text size={"3"} color={"gray"}>
            {position}
          </Text>
        </Flex>
        {links && (
          <Flex className={"mb-2"} gap={"2"}>
            {links.map((item) => (
              <Button
                key={item.label}
                size={"1"}
                color={"tomato"}
                variant={"outline"}
                className={"cursor-pointer"}
              >
                <Link
                  href={item.href}
                  target={"_blank"}
                  rel={"noopener noreferrer"}
                  className={"text-[var(--tomato-9)]"}
                >
                  {item.label}
                </Link>
              </Button>
            ))}
          </Flex>
        )}
        <Flex direction={"column"} gap={"2"}>
          <Flex
            align={"center"}
            gap={"2"}
            className={"cursor-pointer w-fit"}
            style={{ color: "var(--lime-8)" }}
            onClick={() => setOpenDetails((curr) => !curr)}
          >
            <ThickChevronRightIcon
              className={"transition-all"}
              style={{
                transform: openDetails ? "rotate(90deg)" : "rotate(0deg)",
              }}
            />
            <Text size={"2"} weight={"medium"}>
              주요 업무 내용 {openDetails ? "가리기" : "보기"}
            </Text>
          </Flex>
          {openDetails && (
            <Card className={"bg-[var(--sand-2)]"}>
              <ol className={"work-detail-ol rt-r-size-3"}>
                {details.map((detail, index) => (
                  <li key={index} className={"rt-r-size-3"}>
                    <Text weight={"medium"}>{detail.title}</Text>
                    <ul className={"work-detail-ul rt-r-size-3"}>
                      {detail?.descriptions &&
                        detail.descriptions.map((description, index) => (
                          <li key={index} className={"rt-r-size-3"}>
                            <Text>{description}</Text>
                          </li>
                        ))}
                    </ul>
                  </li>
                ))}
              </ol>
            </Card>
          )}
        </Flex>
      </Flex>
    </Grid>
  );
};
